import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import AuthHeader from '../components/auth/AuthHeader';
import SignInForm from '../components/auth/SignInForm';
import SocialButtons from '../components/auth/SocialButtons';
import Divider from '../components/auth/Divider';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft } from 'lucide-react';

// Key for storing login error in localStorage
const LOGIN_ERROR_KEY = 'aura_login_error';

const SignIn = () => {
  const { isAuthenticated, loading, checkAndRedirectIfAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  
  // Get the return path from location state (if it exists)
  const from = location.state?.from || '/dashboard';
  
  // Initialize login error from localStorage if present
  const [loginError, setLoginError] = useState<string | null>(() => {
    const storedError = localStorage.getItem(LOGIN_ERROR_KEY);
    // Clear the stored error immediately to prevent it from persisting indefinitely
    if (storedError) {
      localStorage.removeItem(LOGIN_ERROR_KEY);
      return storedError;
    }
    return null;
  });

  // We don't need to check auth here - the PublicRoute wrapper handles this

  // Handle successful login
  const handleLoginSuccess = () => {
    // The actual navigation will happen in the login function
    console.log('Login successful, navigating to:', from);
  };

  // Handle login error
  const handleLoginError = (error: string) => {
    // Store the error in localStorage so it persists across page reloads
    localStorage.setItem(LOGIN_ERROR_KEY, error);
    setLoginError(error);
  };
  
  // Show loading spinner while checking auth status
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // If user is already authenticated, they will be redirected in the useEffect

  return (
    <AuthLayout
      image={{
        src: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?q=80&w=3000&auto=format&fit=crop",
        alt: "Aura Logo"
      }}
    >
      <AuthHeader
        title={t('signin.welcomeBack')}
        subtitle={t('signin.enterDetails')}
      />

      {/* Social buttons hidden as requested */}

      {loginError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4 shadow-sm flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-red-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {loginError}
        </div>
      )}

      <SignInForm 
        onLoginSuccess={handleLoginSuccess}
        onLoginError={handleLoginError}
        redirectPath={from}
      />

      {/* Back to Home button */}
      <div className="mt-6 text-center">
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('signin.backToHome')}
        </Link>
      </div>
    </AuthLayout>
  );
};

export default SignIn;